<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import { ConstructorProductPrice } from '~/types/Product'

type PricingProps = {
  tieredPrices?: ShopperProductsTypes.ProductPriceTable[] | ConstructorProductPrice
  price?: number
}
const props = defineProps<PricingProps>()
const userStore = useUserStore()
const mappedPrices = computed(() => getMappedPrices(props.tieredPrices))
</script>

<template>
  <div class="pricing" data-testid="pricing">
    <template v-if="mappedPrices">
      <!-- single only strategy -->
      <div v-if="mappedPrices.singlePrice" class="pricing__single" data-testid="pricing__single">
        <span>{{ $t('base_pricing.only_pricing') }}</span>
        <CurrencyFormat :value="mappedPrices.singlePrice" :decimal="0" />
      </div>

      <!-- retail only strategy -->
      <div
        v-else-if="mappedPrices.retailPrice && !mappedPrices.doublePrice && !mappedPrices.memberPrice"
        class="pricing__retail"
        data-testid="pricing__retail"
      >
        <CurrencyFormat :value="mappedPrices.retailPrice" :decimal="0" />
      </div>

      <!-- double and retail strategy -->
      <div
        v-else-if="mappedPrices.doublePrice && mappedPrices.retailPrice"
        class="pricing__double"
        data-testid="pricing__double"
      >
        <CurrencyFormat
          class="pricing__double--amount"
          data-testid="pricing__double--sale"
          :value="mappedPrices.doublePrice"
          :decimal="0"
        />
        <CurrencyFormat
          class="pricing__double--strike"
          data-testid="pricing__double--list"
          :value="mappedPrices.retailPrice"
          :decimal="0"
        />
        <span class="pricing__double--description">{{ $t('base_pricing.list_price') }}</span>
      </div>

      <!-- member and retail strategy (shares the retail class for styling, distinct testid for intent) -->
      <div
        v-else-if="mappedPrices.memberPrice && mappedPrices.retailPrice"
        class="pricing__retail"
        data-testid="pricing__member"
      >
        <CurrencyFormat
          :value="userStore.isLoggedIn ? mappedPrices.memberPrice : mappedPrices.retailPrice"
          :decimal="0"
        />
      </div>
    </template>
    <CurrencyFormat v-else :value="price" :decimal="0" class="pricing__price" data-testid="pricing__fallback" />
  </div>
</template>
<style lang="postcss">
.pricing {
  font-size: var(--text-xlarge);
  .pricing__single {
    color: var(--text-sale);

    span {
      margin-right: var(--space-20);
    }
  }

  .pricing__double {
    .pricing__double--amount {
      color: var(--text-sale);
      margin-right: var(--space-30);
    }

    .pricing__double--strike {
      text-decoration-line: line-through;
    }

    .pricing__double--description {
      margin-left: var(--space-20);
      font-size: var(--text-small);
    }
  }
}
</style>
